import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import billingService from '../../api/billingService';
import customerService from '../../api/customerService';
import { formatCurrency, formatDate } from '../../utils/formatters';

const QuoteList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const preselectedCustomerId = searchParams.get('customerId') || '';
  const [quotes, setQuotes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [userRole, setUserRole] = useState('owner');
  const [tableActions, setTableActions] = useState({});

  useEffect(() => {
    fetchQuotes();
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignTo]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const customerId = searchParams.get('customerId');
    if (customerId) {
      navigate(`/billing/quotes/create?customerId=${encodeURIComponent(customerId)}`, { replace: true });
    }
  }, [location.search, navigate]);

  const safeStr = (val) => {
    if (val == null) return '';
    return typeof val === 'string' ? val : String(val);
  };

  const filteredQuotes = quotes.filter(q => {
    const needle = searchTerm.toLowerCase();
    const qNumber = safeStr(q.quotationNumber).toLowerCase();
    const cName = safeStr(q.customerName || q.customer?.firstname || q.customer?.firstName || `${q.customer?.firstname ?? ''} ${q.customer?.lastname ?? ''}`.trim() || '').toLowerCase();
    const cCompany = safeStr(q.company || q.customerCompany || q.customer?.company || '').toLowerCase();
    const cPhone = safeStr(q.customerPhone || q.phone || q.customer?.phone || '').toLowerCase();
    const productsStr = (Array.isArray(q.items) ? q.items.map(i => safeStr(i.productName || i.product || '').toLowerCase()).join(' ') : '');
    const totalStr = safeStr(q.totalAmount ?? q.total ?? '').toLowerCase();
    return (
      qNumber.includes(needle) ||
      cName.includes(needle) ||
      cCompany.includes(needle) ||
      cPhone.includes(needle) ||
      productsStr.includes(needle) ||
      totalStr.includes(needle)
    );
  });

  const fetchMembers = async () => {
    try {
      const sessionRes = await customerService.getSession();
      if (sessionRes && sessionRes.success) {
        const userInfo = sessionRes.userInfo;
        setUserRole(userInfo.role);
        setTableActions(userInfo.permissions?.tableActions?.quotes || {});
        if (userInfo.role !== 'owner') {
          setAssignTo(userInfo.email);
        }
        const response = await customerService.getAllMembers(userInfo.id);
        if (response && response.data && response.data.ActiveMembers) {
          setMembers(response.data.ActiveMembers);
        }
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const res = await billingService.getQuotes({ assignTo });
      if (res.success) setQuotes(res.data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this quotation?')) {
      try {
        const res = await billingService.deleteQuote(id);
        if (res.success) fetchQuotes();
        else alert(res.message);
      } catch (error) {
        console.error('Error deleting quote:', error);
        alert(error.message);
      }
    }
  };

  const copyPublicLink = (publicId) => {
    const url = `${window.location.origin}/public/quotes/${publicId}`;
    
    // Open in new tab
    window.open(url, '_blank');

    // Copy to clipboard
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url)
        .then(() => alert('Public link opened in new tab and copied to clipboard!'))
        .catch(() => fallbackCopyTextToClipboard(url));
    } else {
      fallbackCopyTextToClipboard(url);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    
    // Avoid scrolling to bottom
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
       // alert('Public link copied to clipboard!');
      } else {
        //alert('Unable to copy link. Please copy it manually: ' + text);
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
     // alert('Unable to copy link. Please copy it manually: ' + text);
    }

    document.body.removeChild(textArea);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-700 mb-1">Quotations</h4>
          <p className="text-muted small mb-0">Manage and track your sales proposals.</p>
        </div>
        <button 
          className="btn btn-primary shadow-sm px-4"
          onClick={() => navigate(preselectedCustomerId ? `/billing/quotes/create?customerId=${encodeURIComponent(preselectedCustomerId)}` : '/billing/quotes/create')}
        >
          <i className="bi bi-plus-lg me-2"></i> Create Quotation
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header border-0 bg-transparent pt-4 px-4">
          <div className="row g-3">
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
                <input 
                  type="text" 
                  className="form-control bg-light border-0" 
                  placeholder="Search quote # or customer..." 
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select 
                className="form-select bg-light border-0" 
                value={assignTo} 
                onChange={e => setAssignTo(e.target.value)}
                disabled={userRole !== 'owner'}
              >
                <option value="">All Members</option>
                {members.map(member => (
                  <option key={member.username} value={member.username}>{member.username}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="table-responsive mt-3">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Quote Number</th>
                <th>Customer</th>
                <th>Date / Valid Until</th>
                <th>Amount</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-5">Loading...</td></tr>
              ) : filteredQuotes.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-5">No quotations found.</td></tr>
              ) : (
                filteredQuotes.map((quote) => (
                  <tr key={quote._id}>
                    <td>
                      <div className="fw-600">{quote.quotationNumber}</div>
                      <div className="text-xs text-muted">ID: {quote._id}</div>
                    </td>
                    <td>
                      <div className="fw-600">{quote.customerName}</div>
                      <div className="text-xs text-muted">{quote.customerPhone}</div>
                    </td>
                    <td>
                      <div className="text-xs fw-600">Issued: {formatDate(quote.quotationDate)}</div>
                      <div className="text-xs text-danger">Until: {formatDate(quote.validUntil)}</div>
                    </td>
                    <td>
                      <div className="fw-600">{formatCurrency(quote.totalAmount)}</div>
                      <div className="text-xs text-muted">Items: {quote.items?.length || 0}</div>
                    </td>
                    <td>
                      <span className={`badge badge-soft-${
                        quote.status === 'active' ? 'success' : 
                        quote.status === 'pending' ? 'warning' : 
                        quote.status === 'rejected' ? 'danger' : 'secondary'
                      }`}>
                        {quote.status?.toUpperCase() || 'PENDING'}
                      </span>
                    </td>

                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        {quote.customerId && (
                          <button className="btn btn-sm btn-light text-dark" onClick={() => navigate(`/billing/customers/${quote.customerId}`)} title="View Customer"><i className="bi bi-person-lines-fill"></i></button>
                        )}
                        {(userRole === 'owner' || tableActions.edit !== false) && (
                          <button className="btn btn-sm btn-light" onClick={() => navigate(`/billing/quotes/edit/${quote._id}`)} title="Edit"><i className="bi bi-pencil"></i></button>
                        )}
                        {(userRole === 'owner' || tableActions.view !== false) && (
                          <>
                            <button className="btn btn-sm btn-light text-primary" onClick={() => navigate(`/billing/quotes/view/${quote._id}`)} title="View"><i className="bi bi-eye"></i></button>
                            <button className="btn btn-sm btn-light text-info" onClick={() => copyPublicLink(quote.publicId)} title="Copy Public Link"><i className="bi bi-share"></i></button>
                          </>
                        )}
                        {(userRole === 'owner' || tableActions.delete !== false) && (
                          <button className="btn btn-sm btn-light text-danger" onClick={() => handleDelete(quote._id)} title="Delete"><i className="bi bi-trash"></i></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuoteList;
